import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { useLikes } from '../../context/LikesContext';

type LikeSubject = 'artist' | 'track';
type LikeButtonVariant = 'icon' | 'pill' | 'badge';
type LikeButtonSize = 'sm' | 'md';

interface LikeButtonProps {
  subjectType: LikeSubject;
  subjectId: number;
  initialCount?: number;
  variant?: LikeButtonVariant;
  size?: LikeButtonSize;
  className?: string;
  label?: string;
  onCountChange?: (nextCount: number) => void;
}

export function LikeButton({
  subjectType,
  subjectId,
  initialCount = 0,
  variant = 'pill',
  size = 'md',
  className,
  label,
  onCountChange,
}: LikeButtonProps) {
  const { user, token } = useAuth();
  const { addToast } = useToast();
  const { isArtistLiked, isTrackLiked, setArtistLike, setTrackLike } = useLikes();
  const [count, setCount] = useState(initialCount);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    setCount(initialCount);
  }, [initialCount, subjectId]);

  const isLiked = subjectType === 'artist' ? isArtistLiked(subjectId) : isTrackLiked(subjectId);

  const buttonLabel = useMemo(() => {
    if (label) return label;
    return isLiked ? 'Liked' : 'Like';
  }, [isLiked, label]);

  const heartIcon = isLiked ? 'ri-heart-fill' : 'ri-heart-line';

  const buttonClasses =
    variant === 'icon'
      ? `${isLiked ? 'bg-rose-500 border-rose-500 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'} ${
          size === 'sm' ? 'w-9 h-9 text-base' : 'w-10 h-10 text-lg'
        } inline-flex items-center justify-center rounded-full border transition-colors`
      : variant === 'badge'
        ? `${isLiked ? 'bg-rose-50 border-rose-200 text-rose-600' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'} ${
            size === 'sm' ? 'text-xs px-2.5 py-1' : 'text-sm px-3 py-1.5'
          } inline-flex items-center gap-1.5 rounded-full border font-medium transition-colors`
        : `${isLiked ? 'bg-rose-50 border-rose-200 text-rose-600' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'} ${
            size === 'sm' ? 'text-xs px-3 py-1.5' : 'text-sm px-4 py-2'
          } inline-flex items-center rounded-full border font-medium transition-colors`;

  const subjectLabel = subjectType === 'artist' ? 'artist' : 'track';

  const handleClick = async () => {
    if (!user || !token) {
      addToast({
        message: `Create a free account to like this ${subjectLabel}.`,
        actionLabel: 'Create account',
        actionHref: '/signup',
      });
      return;
    }
    if (pending) return;
    setPending(true);
    try {
      const shouldLike = !isLiked;
      const nextCount =
        subjectType === 'artist'
          ? await setArtistLike(subjectId, shouldLike)
          : await setTrackLike(subjectId, shouldLike);
      setCount(nextCount);
      onCountChange?.(nextCount);
    } catch (error) {
      console.error('Failed to toggle like', error);
      addToast({
        message: `Unable to update your like for this ${subjectLabel}. Please try again.`,
        variant: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      aria-pressed={isLiked}
      aria-label={`${buttonLabel} ${subjectLabel}`}
      className={`${buttonClasses} ${pending ? 'opacity-70 cursor-not-allowed' : ''} ${className ?? ''}`}
    >
      <i
        className={`${heartIcon} ${variant === 'icon' ? '' : variant === 'badge' ? '' : 'mr-2'} ${
          variant === 'pill' ? 'text-base' : ''
        }`}
      />
      {variant === 'pill' ? (
        <>
          <span>{buttonLabel}</span>
          <span className="ml-2 text-xs text-gray-500">{count}</span>
        </>
      ) : null}
      {variant === 'badge' ? (
        <span className="text-xs text-gray-500">{count}</span>
      ) : null}
    </button>
  );
}
